/**
 * OneSignal Plugin Configuration for Vue.js
 * Utilise le plugin officiel onesignal-vue
 * Documentation: https://documentation.onesignal.com/docs/en/vue-js-setup
 */

import Vue from 'vue'
import OneSignalVue from 'onesignal-vue'

const ONESIGNAL_APP_ID = '44881627-59b8-4941-8c49-7aef110bbf86'

// Installer le plugin officiel OneSignal
Vue.use(OneSignalVue)

/**
 * Helper pour identifier un utilisateur avec Auth0
 * @param {Object} user - Objet utilisateur Auth0
 */
export async function identifyOneSignalUser(user) {
  const hostname = window.location.hostname
  
  if (hostname !== 'voxdub.com' && hostname !== 'www.voxdub.com') {
    return
  }
  
  if (!user || !Vue.prototype.$OneSignal) return

  try {
    // Vérifier que l'utilisateur a un ID valide
    const externalUserId = user.sub || user.email
    
    if (!externalUserId) {
      console.warn('[OneSignal] No valid user ID (sub or email) - skipping identification')
      return
    }
    
    // Utiliser login pour identifier l'utilisateur (SDK v16)
    await Vue.prototype.$OneSignal.login(externalUserId)
    
    // Définir les tags utilisateur
    const tags = {
      role: user.role || 'free',
      created_at: user.created_at || new Date().toISOString(),
      locale: user.locale || 'fr',
      last_login: new Date().toISOString()
    }
    
    // Ajouter email et name seulement s'ils existent
    if (user.email) {
      tags.email = user.email
    }
    if (user.name) {
      tags.name = user.name
    }
    
    await Vue.prototype.$OneSignal.User.addTags(tags)

    console.log('[OneSignal] User identified:', externalUserId)
  } catch (error) {
    console.error('[OneSignal] User identification error:', error)
  }
}

/**
 * Helper pour tracker un événement
 * @param {string} eventName - Nom de l'événement
 * @param {Object} properties - Propriétés de l'événement
 */
export async function trackOneSignalEvent(eventName, properties = {}) {
  if (!Vue.prototype.$OneSignal) return

  try {
    const eventTag = { [`last_${eventName}`]: new Date().toISOString() }
    
    await Vue.prototype.$OneSignal.User.addTags(eventTag)
    
    if (Object.keys(properties).length > 0) {
      await Vue.prototype.$OneSignal.User.addTags(properties)
    }
    
    console.log('[OneSignal] Event tracked:', eventName, properties)
  } catch (error) {
    console.error('[OneSignal] Track event error:', error)
  }
}

/**
 * Helper pour mettre à jour les tags
 * @param {Object} tags - Tags à mettre à jour
 */
export async function updateOneSignalTags(tags) {
  if (!Vue.prototype.$OneSignal) return

  try {
    await Vue.prototype.$OneSignal.User.addTags(tags)
    console.log('[OneSignal] Tags updated:', tags)
  } catch (error) {
    console.error('[OneSignal] Update tags error:', error)
  }
}

/**
 * Helper pour définir le statut d'abonnement
 * @param {string} status - Statut: 'free', 'pro', 'enterprise'
 */
export async function setOneSignalSubscriptionStatus(status) {
  if (!Vue.prototype.$OneSignal) return

  try {
    await Vue.prototype.$OneSignal.User.addTags({
      subscription_status: status,
      subscription_updated_at: new Date().toISOString()
    })
    console.log('[OneSignal] Subscription status updated:', status)
  } catch (error) {
    console.error('[OneSignal] Set subscription status error:', error)
  }
}

/**
 * Helper pour logout
 */
export async function logoutOneSignal() {
  if (!Vue.prototype.$OneSignal) return

  try {
    await Vue.prototype.$OneSignal.logout()
    await Vue.prototype.$OneSignal.User.removeTags(['email', 'name', 'role', 'subscription_status'])
    console.log('[OneSignal] User logged out')
  } catch (error) {
    console.error('[OneSignal] Logout error:', error)
  }
}

/**
 * Initialise OneSignal avec la configuration
 */
export async function initOneSignal() {
  const hostname = window.location.hostname
  
  if (hostname !== 'voxdub.com' && hostname !== 'www.voxdub.com') {
    console.log('[OneSignal] Skipped - not on production domain')
    return false
  }
  
  try {
    await Vue.prototype.$OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: false
    })
    
    console.log('[OneSignal] Initialized successfully')
    return true
  } catch (error) {
    console.error('[OneSignal] Initialization error:', error)
    return false
  }
}

// Export de l'App ID pour utilisation ailleurs
export { ONESIGNAL_APP_ID }

// Export par défaut pour compatibilité
export default {
  install(Vue) {
    // Le plugin est déjà installé via Vue.use(OneSignalVue)
    // On ajoute juste des helpers globaux
    Vue.prototype.$oneSignalHelpers = {
      identifyUser: identifyOneSignalUser,
      trackEvent: trackOneSignalEvent,
      updateTags: updateOneSignalTags,
      setSubscriptionStatus: setOneSignalSubscriptionStatus,
      logout: logoutOneSignal
    }
  }
}
